import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TranslateActivityCategoryPipeModule } from '@wilson/activity-categories/data';
import { compareActivityCategoryFn } from '@wilson/activities/util';
import { AuthorizationModule } from '@wilson/authorization';
import { ClientsV2Service, generateOptions } from '@wilson/clients/services';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { WhiteSpaceValidator } from '@wilson/forms';
import {
  Activity,
  ActivityCategory,
  FeatureName,
  GeoLocation,
  OperationStatus,
  Profession,
  RoleAction,
  RolePermissionSubject,
  Sector,
} from '@wilson/interfaces';
import {
  LocationSelectsComponent,
  ViaPointButtonComponent,
} from '@wilson/non-domain-specific/activities-helpers/components';
import { TimePickerAutoFormatDirective } from '@wilson/non-domain-specific/time-picker-auto-format';
import {
  SelectedAgreementNamePipe,
  SelectedAgreementPipe,
} from '@wilson/pipes';
import { ProfessionHelperService } from '@wilson/profession/core';
import { SectorState } from '@wilson/sectors';
import {
  GanttIsActivityEndLocationMismatch,
  GanttIsActivityStartLocationMismatch,
} from '@wilson/shift-timeline/pipes';
import { NzCascaderModule, NzCascaderOption } from 'ng-zorro-antd/cascader';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';
import { ActivityDrawerLocationMismatchComponent } from '../activity-drawer-location-mismatch/activity-drawer-location-mismatch.component';
import { TimelineActivityDrawerService } from '../timeline-activity-drawer.service';
import { DateFieldsHandlerService } from './date-fields-handler.service';
import { LocalizedDateFormatPipe } from '@wilson/localization';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type FormValue = Partial<{
  name: string | undefined | null;
  id: string | undefined | null;
  startLocation: GeoLocation | null | undefined;
  endLocation: GeoLocation | null | undefined;
  viaPointIds: string[] | null;
  startDateTime: string | null;
  endDateTime: string | null;
  activityCategory: ActivityCategory | null;
  agreement: string[] | undefined | null;
  professionId: string | undefined | null;
  orderNumber: string | undefined | null;
  wagonNumber: string | undefined | null;
  locomotiveNumber: string | undefined | null;
}>;

@Component({
  selector: 'wilson-activity-drawer-form',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    LocationSelectsComponent,
    ViaPointButtonComponent,
    NzFormModule,
    NzSelectModule,
    NzInputModule,
    NzTimePickerModule,
    TimePickerAutoFormatDirective,
    TranslateActivityCategoryPipeModule,
    AuthorizationModule,
    NzCascaderModule,
    SelectedAgreementNamePipe,
    ActivityDrawerLocationMismatchComponent,
    FeatureFlagsModule,
    NzTooltipDirective,
    LocalizedDateFormatPipe,
  ],
  providers: [DateFieldsHandlerService, SelectedAgreementPipe],
  templateUrl: './activity-drawer-form.component.html',
  styleUrls: ['./activity-drawer-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityDrawerFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private dateFieldsHandlerService = inject(DateFieldsHandlerService);
  private clientsService = inject(ClientsV2Service);
  private selectedAgreementPipe = inject(SelectedAgreementPipe);
  activityDrawerService = inject(TimelineActivityDrawerService);
  isActivityStartLocationMismatchPipe = inject(
    GanttIsActivityStartLocationMismatch,
  );
  isActivityEndLocationMismatchPipe = inject(
    GanttIsActivityEndLocationMismatch,
  );
  private readonly professionHelperService = inject(ProfessionHelperService);

  OperationStatus = OperationStatus;
  readonly displayPreviousLocationMismatchWarning = signal(false);
  readonly displayNextLocationMismatchWarning = signal(false);
  nextActivityMismatchingStartingLocation!: GeoLocation | null | undefined;
  previousActivityMismatchingEndLocation!: GeoLocation | null | undefined;

  @Input()
  activity!: Activity;

  @Input()
  activities!: undefined | null | Activity[];

  @Input()
  activityCategories!: ActivityCategory[];

  @Input()
  set isDrawerOpened(value: boolean) {
    if (!value) {
      this.form.reset();
    }
  }

  @Input()
  set isDisabled(value: boolean) {
    this.readonly = value;
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Output()
  value = new EventEmitter<FormValue>();

  @Output()
  updateActivity = new EventEmitter();

  formControls = {
    id: new FormControl('', {
      validators: [Validators.required],
    }),
    name: new FormControl('', {
      validators: [WhiteSpaceValidator],
    }),
    startLocation: new FormControl<GeoLocation | null | undefined>(null, {
      validators: [Validators.required],
    }),
    endLocation: new FormControl<GeoLocation | null | undefined>(null, {
      validators: [Validators.required],
    }),
    viaPointIds: new FormControl<string[] | null>(null),
    startDateTime: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
    endDateTime: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
    activityCategory: new FormControl<ActivityCategory | null | undefined>(
      null,
      {
        validators: [Validators.required],
      },
    ),
    agreement: new FormControl<string[] | null | undefined>(null),
    professionId: new FormControl<string | null | undefined>(null),
    orderNumber: new FormControl<string | null | undefined>(null, [
      Validators.maxLength(255),
    ]),
    wagonNumber: new FormControl<string | null | undefined>(null, [
      Validators.maxLength(255),
    ]),
    locomotiveNumber: new FormControl<string | null | undefined>(null, [
      Validators.maxLength(255),
    ]),
  };

  form = new FormGroup(this.formControls);

  readonly isServiceActivity = signal(false);
  readonly originalStartDateTime = signal<string | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  isEndDateNextDay$ = this.dateFieldsHandlerService.isEndDateNextDayStream(
    this.form,
    this.formControls.startDateTime,
    this.formControls.endDateTime,
  );

  readonly = false;
  sector$!: Observable<Sector>;

  agreementOptions$!: Observable<NzCascaderOption[]>;
  agreementName$!: Observable<string>;

  compareActivityFn = compareActivityCategoryFn;

  private unsubSubject = new Subject();
  FeatureName = FeatureName;
  public professions$: Observable<
    (Profession & { nameTranslationKey: string })[]
  > = of([]);
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activities']) {
      this.determineMismatchLocations();
    }
  }

  ngOnInit(): void {
    this.originalStartDateTime.set(this.activity.startDatetime);
    this.agreementOptions$ = this.clientsService
      .getMany({ relations: ['agreements'], limit: 0 })
      .pipe(
        map((result) => generateOptions(result.data)),
        shareReplay(),
        tap((options: NzCascaderOption[]) => {
          if (this.activity.agreementId) {
            this.form.patchValue({
              agreement: this.selectedAgreementPipe.transform(
                this.activity.agreementId,
                options,
              ),
            });
          }
        }),
      );

    this.sector$ = this.store
      .select(SectorState.sectors)
      .pipe(map((sectors) => sectors[0]));

    this.isServiceActivity.set(!!this.activity.serviceId);
    this.form.patchValue({
      id: this.activity.id as string,
      activityCategory: this.activity.activityCategory,
      name: this.activity.name,
      startLocation: this.activity.startLocation,
      endLocation: this.activity.endLocation,
      viaPointIds: this.activity.viaLocationIds,
      startDateTime: this.activity.startDatetime
        ? new Date(this.activity.startDatetime)
        : null,
      endDateTime: this.activity.endDatetime
        ? new Date(this.activity.endDatetime)
        : null,
      agreement: null,
      professionId: this.activity.professionId || null,
      orderNumber: this.activity.orderNumber || null,
      wagonNumber: this.activity.wagonNumber || null,
      locomotiveNumber: this.activity.locomotiveNumber || null,
    });
    this.getProfessions();

    merge(
      this.formControls.startDateTime.valueChanges.pipe(
        startWith(this.formControls.startDateTime.value),
        distinctUntilChanged(
          (previous, current) => previous?.getTime() === current?.getTime(),
        ),
      ),
      this.formControls.endDateTime.valueChanges.pipe(
        startWith(this.formControls.endDateTime.value),
        distinctUntilChanged(
          (previous, current) => previous?.getTime() === current?.getTime(),
        ),
      ),
    )
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFormValue());
  }

  updateFormValue(): void {
    this.value.emit({
      ...this.form.value,
      ...this.dateFieldsHandlerService.setCorrectStartEndDateTime({
        startDateTime: this.form.value.startDateTime,
        originalStartDateTime: this.activity.startDatetime,
        endDateTime: this.form.value.endDateTime,
      }),
    });
    this.updateActivity.emit();
  }

  public getProfessions(): void {
    this.professions$ = this.professionHelperService.getProfessions();
  }

  setViaPointIds(viaLocationIds: string[]): void {
    this.setValueAndUpdateFormState({
      viaPointIds: viaLocationIds,
    });
    this.updateFormValue();
  }

  setStartLocation(event: { location: GeoLocation | null }): void {
    this.setValueAndUpdateFormState({
      startLocation: event.location,
    });
    this.updateFormValue();
  }

  setEndLocation(event: { location: GeoLocation | null }): void {
    this.setValueAndUpdateFormState({
      endLocation: event.location,
    });
    this.updateFormValue();
  }

  determineMismatchLocations(): void {
    if (this.activities) {
      const currentActivityIndex = this.activities?.findIndex(
        (activity) => activity.id === this.activity.id,
      );

      const isStartLocationMismatch =
        this.isActivityStartLocationMismatchPipe.transform(
          this.activities,
          this.activity.id as string,
        );

      const isEndLocationMismatch =
        this.isActivityEndLocationMismatchPipe.transform(
          this.activities,
          this.activity.id as string,
        );

      if (isStartLocationMismatch) {
        const previousActivityEndLocation =
          this.activities[currentActivityIndex - 1]?.endLocation;
        this.displayPreviousLocationMismatchWarning.set(true);
        this.previousActivityMismatchingEndLocation =
          previousActivityEndLocation;
      }

      if (isEndLocationMismatch) {
        const nextActivityStartLocation =
          this.activities[currentActivityIndex + 1]?.startLocation;
        this.displayNextLocationMismatchWarning.set(true);
        this.nextActivityMismatchingStartingLocation =
          nextActivityStartLocation;
      }
      this.cdr.detectChanges();
    }
  }

  updateActivityStartLocation(): void {
    this.displayPreviousLocationMismatchWarning.set(false);
    this.setValueAndUpdateFormState({
      startLocation: this.previousActivityMismatchingEndLocation,
    });
    this.updateFormValue();
  }

  updateActivityEndLocation(): void {
    this.displayNextLocationMismatchWarning.set(false);
    this.setValueAndUpdateFormState({
      endLocation: this.nextActivityMismatchingStartingLocation,
    });
    this.updateFormValue();
  }

  private setValueAndUpdateFormState(
    payload: Partial<typeof this.form.value>,
  ): void {
    this.form.patchValue(payload);
    this.form.markAsDirty();
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.unsubSubject.next(null);
  }
}
